import { minorToMajor } from "../i18n/utils/helpers";

export const FLAT_FEE_PRICING_MODEL = "flat_fee";

export const CHECKOUT_AMOUNT_EPSILON = 0.01;

export function amountsRoughlyEqual(a, b, epsilon = CHECKOUT_AMOUNT_EPSILON) {
  return Math.abs(Number(a ?? 0) - Number(b ?? 0)) < epsilon;
}

const TECHNICAL_ID_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)+$/i;

export function formatTechnicalId(value) {
  if (!value || typeof value !== "string") return "";
  return value
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractStripeItemName(description) {
  if (!description) return "";
  return description
    .replace(/^\d+\s*×\s*/i, "")
    .replace(/\s*\(at.*\)$/i, "")
    .trim();
}

export function looksTechnicalId(value) {
  if (!value || typeof value !== "string") return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.includes(" ")) return false;
  return TECHNICAL_ID_PATTERN.test(trimmed);
}

/**
 * Human-readable label for plan/addon/charge catalog or estimate rows (all providers).
 */
export function resolveCatalogItemDisplayName(item, options = {}) {
  const {
    billingPlatformName = "",
    fallbackId = "",
    index = 0,
  } = options;

  const isStripe = (billingPlatformName || item?.billingPlatformName || "")
    .toString()
    .toLowerCase() === "stripe";

  if (isStripe) {
    const stripeLabel = extractStripeItemName(item?.description);
    if (stripeLabel) return stripeLabel;
  }

  const candidates = [
    item?.externalName,
    item?.description,
    item?.planName,
    item?.name,
    item?.itemName,
    item?.displayName,
    item?.itemPriceName,
    !looksTechnicalId(item?.id) ? item?.id : null,
    !looksTechnicalId(item?.itemPriceId) ? item?.itemPriceId : null,
    fallbackId,
  ];

  for (const raw of candidates) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const formatted = formatTechnicalId(raw);
    if (!formatted) continue;
    if (!looksTechnicalId(raw) || formatted.includes(" ")) {
      return formatted;
    }
  }

  const fromFallback = formatTechnicalId(fallbackId);
  if (fromFallback) return fromFallback;

  return `Item ${index + 1}`;
}

export function findCatalogItemPrice(platformItemDetails, itemPriceId) {
  if (!itemPriceId || !Array.isArray(platformItemDetails)) return null;
  return (
    platformItemDetails.find(
      (p) => p.id === itemPriceId || p.code === itemPriceId
    ) || null
  );
}

export function normalizePricingModel(itemPrice) {
  return (itemPrice?.pricingModel || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function supportsQuantityFromPricingModel(billingPlatformName, itemPrice) {
  const model = normalizePricingModel(itemPrice);
  const platform = (billingPlatformName || "").toLowerCase();

  if (!model) return true;
  if (model === FLAT_FEE_PRICING_MODEL || model === "fixed") return false;
  if (model === "usage" || model === "metered") return false;

  if (platform === "chargebee") {
    return model !== FLAT_FEE_PRICING_MODEL;
  }

  return true;
}

export function supportsLineItemQuantity(billingPlatformName, itemPrice) {
  if (!itemPrice) return true;
  if (typeof itemPrice.quantityAllowed === "boolean") {
    return itemPrice.quantityAllowed;
  }
  return supportsQuantityFromPricingModel(billingPlatformName, itemPrice);
}

export function resolveLineItemQuantity(billingPlatformName, itemPrice, requestedQuantity) {
  if (!supportsLineItemQuantity(billingPlatformName, itemPrice)) {
    return 1;
  }
  const qty = Number(requestedQuantity);
  return Number.isFinite(qty) && qty >= 1 ? qty : 1;
}

/** Minor units from catalog or hydrated addon row. */
export function getAddonUnitPriceMinor(addon) {
  return Number(addon?.unitPrice ?? addon?.price ?? 0);
}

function isTaxExclusivePriceType(priceType) {
  return priceType === "tax_exclusive";
}

/**
 * Display amounts for estimate API line items (some providers omit unitAmount on flat fees).
 * For tax_exclusive estimates, line totals are shown pre-tax so rows, subtotal, and tax add up.
 */
export function resolveEstimateLineDisplay(lineItem, options = {}) {
  const priceType = options.priceType;
  const taxExclusive = isTaxExclusivePriceType(priceType);

  const qty = Math.max(1, Number(lineItem?.quantity ?? 1));
  const lineTotal = Number(lineItem?.totalAmount ?? 0);
  const lineTax = Number(lineItem?.taxAmount ?? 0);
  const unitAmount = Number(lineItem?.unitAmount ?? 0);

  if (lineTotal < 0) {
    return {
      displayTotal: lineTotal,
      displayUnit: lineTotal / qty,
      qty,
      showQuantityBreakdown: qty > 1,
    };
  }

  let displayTotal;
  let displayUnit;

  if (taxExclusive) {
    if (lineTax > 0 && lineTotal > 0) {
      displayTotal = Math.max(0, lineTotal - lineTax);
    } else {
      displayTotal = lineTotal > 0 ? lineTotal : unitAmount * qty;
    }
    displayUnit =
      unitAmount > 0 ? unitAmount : displayTotal > 0 ? displayTotal / qty : 0;
  } else {
    displayTotal = lineTotal > 0 ? lineTotal : unitAmount * qty;
    displayUnit =
      unitAmount > 0 ? unitAmount : lineTotal > 0 ? lineTotal / qty : 0;
  }

  const showQuantityBreakdown = qty > 1 && displayUnit > 0;
  return { displayTotal, displayUnit, qty, showQuantityBreakdown };
}

export function indexEstimateLinesByEntityId(lineItems) {
  const map = new Map();
  for (const line of lineItems || []) {
    const keys = [line?.entityId, line?.id, line?.itemPriceId].filter(Boolean);
    for (const key of keys) {
      map.set(key, line);
    }
  }
  return map;
}

/** Resolve a catalog/estimate row for a configured line item price id. */
export function resolveCatalogOrHydratedPrice(platformItemDetails, itemPriceId, hydrated) {
  const catalog = findCatalogItemPrice(platformItemDetails, itemPriceId);
  if (catalog) return catalog;
  if (!hydrated) return null;
  return {
    id: itemPriceId,
    code: itemPriceId,
    planName: hydrated.planName || hydrated.externalName,
    externalName: hydrated.externalName || hydrated.planName,
    description: hydrated.description,
    price: hydrated.unitPrice ?? hydrated.price,
    currencyCode: hydrated.currencyCode,
    periodUnit: hydrated.periodUnit,
    pricingModel: hydrated.pricingModel,
    quantityAllowed: hydrated.quantityAllowed,
    billingPlatformName: hydrated.billingPlatformName,
  };
}

export function sumEstimateLineTotals(lineItems, priceType) {
  return (lineItems || []).reduce((acc, item) => {
    const raw = Number(item?.totalAmount ?? 0);
    if (raw <= 0) return acc;
    const { displayTotal } = resolveEstimateLineDisplay(item, { priceType });
    return acc + displayTotal;
  }, 0);
}

/** Subtotal, tax, and total due aligned with estimate priceType (tax_exclusive vs inclusive). */
export function computeEstimateSummaryTotals(estimatedOrder) {
  if (!estimatedOrder) return null;

  const priceType = estimatedOrder.priceType;
  const isTaxExclusive = isTaxExclusivePriceType(priceType);
  const totalTaxAmount = Number(estimatedOrder.totalTaxAmount ?? 0);
  const totalDue = Number(estimatedOrder.futureInvoiceAmountDue ?? 0);
  const linesSubtotal = sumEstimateLineTotals(
    estimatedOrder.lineItems,
    priceType
  );

  const invoiceSubtotal = Number(estimatedOrder.invoiceSubtotal ?? 0);

  let subtotal;
  if (isTaxExclusive) {
    subtotal =
      invoiceSubtotal > 0
        ? invoiceSubtotal
        : linesSubtotal > 0
          ? linesSubtotal
          : Math.max(0, totalDue - totalTaxAmount);
  } else {
    subtotal =
      invoiceSubtotal > 0
        ? invoiceSubtotal
        : linesSubtotal > 0
          ? linesSubtotal
          : totalDue;
  }

  return {
    subtotal,
    totalTaxAmount,
    totalDue,
    isTaxExclusive,
    priceType,
    linesSubtotal,
    invoiceSubtotal,
  };
}

/** Sum of plan + selected add-on display amounts from estimate (or catalog fallback). */
export function computeSelectedItemsBaseline({
  planId,
  platformItem,
  selectedAddons = [],
  estimateLinesById,
  priceType,
  currencyCode,
}) {
  const displayOptions = { priceType };
  const planLine = estimateLinesById?.get?.(planId);
  const planDisplay = planLine
    ? resolveEstimateLineDisplay(planLine, displayOptions)
    : null;
  const planAmount = planDisplay
    ? planDisplay.displayTotal
    : minorToMajor(platformItem?.price || 0, currencyCode);

  const addonsAmount = (selectedAddons || []).reduce((acc, addon) => {
    const line = estimateLinesById?.get?.(addon.itemPriceId);
    if (line) {
      return (
        acc + resolveEstimateLineDisplay(line, displayOptions).displayTotal
      );
    }
    return (
      acc +
      minorToMajor(getAddonUnitPriceMinor(addon), currencyCode) *
        (addon.quantity || 1)
    );
  }, 0);

  return planAmount + addonsAmount;
}

/** Recurring charge after promotions: plan + add-ons (+ invoice tax when tax_exclusive). */
export function computeRecurringChargeTotal(
  baselineSubtotal,
  { isTaxExclusive = false, totalTaxAmount = 0 } = {}
) {
  const base = Number(baselineSubtotal ?? 0);
  if (!base) return 0;
  return isTaxExclusive ? base + Number(totalTaxAmount ?? 0) : base;
}

/** Pre-discount cart total for display when coupons/promotions apply. */
export function resolvePreDiscountSubtotal({
  estimatedOrder,
  baselineSubtotal,
  catalogBaseline,
}) {
  const invoiceSubtotal = Number(estimatedOrder?.invoiceSubtotal ?? 0);
  const baseline = Number(baselineSubtotal ?? 0);
  const catalog = Number(catalogBaseline ?? 0);
  return Math.max(invoiceSubtotal, baseline, catalog);
}

/**
 * Discount from billing platform totals, catalog delta, or negative estimate lines.
 */
export function computeCheckoutDiscount({
  estimatedOrder,
  baselineSubtotal,
  summaryTotals,
  couponCodes = [],
  catalogBaseline,
}) {
  if (!estimatedOrder) return 0;

  const explicitDiscount = (estimatedOrder.lineItems || []).reduce((acc, li) => {
    const amt = Number(li?.totalAmount ?? 0);
    return amt < 0 ? acc + Math.abs(amt) : acc;
  }, 0);

  const hasCoupon = Array.isArray(couponCodes) && couponCodes.length > 0;
  const apiDiscount = Number(estimatedOrder?.totalDiscountAmount ?? 0);

  if (hasCoupon) {
    const isTaxExclusive = summaryTotals?.isTaxExclusive ?? false;
    const totalTaxAmount = Number(summaryTotals?.totalTaxAmount ?? 0);
    const totalDue = Number(summaryTotals?.totalDue ?? 0);
    const normalizedDue = isTaxExclusive ? totalDue - totalTaxAmount : totalDue;

    const preDiscount = resolvePreDiscountSubtotal({
      estimatedOrder,
      baselineSubtotal,
      catalogBaseline,
    });
    const inferredDiscount = preDiscount - normalizedDue;

    const candidates = [apiDiscount, inferredDiscount].filter(
      (d) => d > CHECKOUT_AMOUNT_EPSILON
    );
    if (candidates.length > 0) {
      return Math.max(...candidates);
    }
    if (explicitDiscount > CHECKOUT_AMOUNT_EPSILON) {
      return explicitDiscount;
    }
    return 0;
  }

  if (explicitDiscount > CHECKOUT_AMOUNT_EPSILON) {
    return explicitDiscount;
  }

  return 0;
}

export function shouldShowSubtotalRow(subtotal, totalDue, discount) {
  if (discount > CHECKOUT_AMOUNT_EPSILON) return true;
  return !amountsRoughlyEqual(subtotal, totalDue);
}
